import {
  Alert,
  AlertTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { ChangeEvent, useEffect, useState } from 'react'

import CustomDialog from '~/components/customDialog'
import { TAgent } from '~/features/agents/models'
import AssistantSelectionList from '~/features/campaigns/components/assistantSelectionList'
import {
  ECampaignAgentAccessScope,
  ECampaignChannel,
} from '~/features/campaigns/models'
import {
  getAgentUnsupportedChannels,
  getCampaignChannelLabel,
} from '~/features/campaigns/utils'
import rem from '~/utils/ui'

type TCampaignShareDialogProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (payload: {
    agentAccessScope: ECampaignAgentAccessScope
    agentIds: string[]
  }) => Promise<void> | void
  isSubmitting: boolean
  isSharedCampaign: boolean
  assistants: TAgent[]
  currentAgentId: string
  selectedCampaignAssistantIds: string[]
  campaignChannels: ECampaignChannel[]
}

export default function CampaignShareDialog({
  isOpen,
  onClose,
  onSave,
  isSubmitting,
  isSharedCampaign,
  assistants,
  currentAgentId,
  selectedCampaignAssistantIds,
  campaignChannels,
}: TCampaignShareDialogProps) {
  const [isSharedScopeEnabled, setIsSharedScopeEnabled] = useState(false)
  const [selectedAssistantIds, setSelectedAssistantIds] = useState<string[]>([])

  useEffect(() => {
    if (!isOpen) return
    setIsSharedScopeEnabled(isSharedCampaign)
    setSelectedAssistantIds(selectedCampaignAssistantIds)
  }, [isOpen, isSharedCampaign, selectedCampaignAssistantIds])

  const isSharingEnabled = isSharedCampaign || isSharedScopeEnabled

  const handleSharedScopeToggle = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    if (isSharedCampaign) return
    setIsSharedScopeEnabled(checked)
  }

  const handleAssistantToggle = (assistantId: string) => {
    setSelectedAssistantIds((prev) =>
      prev.includes(assistantId)
        ? prev.filter((id) => id !== assistantId)
        : [...prev, assistantId],
    )
  }

  const selectableItems = assistants.map((assistant) => {
    const assistantId = String(assistant.id)
    const isSelected = selectedAssistantIds.includes(assistantId)
    const unsupportedChannels = getAgentUnsupportedChannels(
      assistant,
      campaignChannels,
    )
    const isChannelCompatible = unsupportedChannels.length === 0
    const isSelectionDisabled =
      !isSharingEnabled || (!isSelected && !isChannelCompatible)

    return {
      id: assistantId,
      label:
        assistant.agentReferenceName ||
        assistant.businessName ||
        assistant.agentName ||
        assistantId,
      checked: isSelected,
      disabled: isSelectionDisabled,
      tooltip: !isChannelCompatible
        ? `This assistant doesn't have ${unsupportedChannels
            .map(getCampaignChannelLabel)
            .join(', ')} enabled.`
        : '',
      dimmed: !isSelected && !isChannelCompatible,
    }
  })

  const selectableEnabledIds = selectableItems
    .filter((item) => !item.disabled)
    .map((item) => item.id)
  const selectedEnabledCount = selectableEnabledIds.filter((id) =>
    selectedAssistantIds.includes(id),
  ).length

  const handleSave = async () => {
    const enabledAgentIds = Array.from(
      new Set([currentAgentId, ...selectedAssistantIds].filter(Boolean)),
    )

    await onSave({
      agentAccessScope: isSharedScopeEnabled
        ? ECampaignAgentAccessScope.shared
        : ECampaignAgentAccessScope.singleAgent,
      agentIds: enabledAgentIds,
    })
  }

  return (
    <CustomDialog
      isOpen={isOpen}
      onClose={onClose}
      onCancel={onClose}
      title="Share Campaign"
      description="Share this campaign with assistants from your organization."
      isDisabled={isSubmitting}
      onConfirm={handleSave}
      cancelText="Cancel"
      confirmText="Save"
      maxWidth={760}
    >
      <Stack spacing={2} sx={{ marginTop: rem(4) }}>
        <FormControlLabel
          control={
            <Switch
              checked={isSharingEnabled}
              onChange={handleSharedScopeToggle}
              disabled={isSharedCampaign}
            />
          }
          label="Shared with other assistants"
        />

        {!isSharedCampaign && (
          <Alert severity="warning">
            <AlertTitle>Important</AlertTitle>
            This action cannot be undone. Once a campaign is shared, it cannot
            be switched back to single assistant mode.
          </Alert>
        )}

        <Divider />
        <Typography variant="body1" color="text.secondary">
          Assistant List:
        </Typography>

        <AssistantSelectionList
          items={selectableItems}
          onToggle={(assistantId) => handleAssistantToggle(assistantId)}
          showSelectAll
          allSelected={
            selectableEnabledIds.length > 0 &&
            selectedEnabledCount === selectableEnabledIds.length
          }
          someSelected={selectedEnabledCount > 0}
          disableSelectAll={
            !isSharingEnabled || selectableEnabledIds.length === 0
          }
          onToggleAll={(checked) => {
            if (!checked) {
              setSelectedAssistantIds([])
              return
            }
            setSelectedAssistantIds(selectableEnabledIds)
          }}
        />
      </Stack>
    </CustomDialog>
  )
}
